// redux/services/newsService/news.Services.js

import { baseApi } from "../baseApi";

export const NewsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ======================
    // CREATE NEWS
    // ======================
    createNews: builder.mutation({
      query: (data) => ({
        url: "/news-create",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["News"],
    }),

    // ======================
    // GET ALL NEWS (with pagination)
    // ======================
    getNews: builder.query({
      query: ({
        page = 1,
        limit = 10,
        search = "",
        category = "",
        status = "",
        featured = "",
      } = {}) =>
        `/news-list?page=${page}&limit=${limit}&search=${search}&category=${category}&status=${status}&featured=${featured}`,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: "News", id })),
              { type: "News", id: "LIST" },
            ]
          : [{ type: "News", id: "LIST" }],
    }),

    // ======================
    // GET SINGLE NEWS BY ID
    // ======================
    getSingleNews: builder.query({
      query: (id) => `/news-details/${id}`,
      providesTags: (result, error, id) => [{ type: "News", id }],
    }),

    // ======================
    // GET NEWS BY SLUG
    // ======================
    getNewsBySlug: builder.query({
      query: (slug) => `/news-slug/${slug}`,
      providesTags: (result, error, slug) => [{ type: "News", slug }],
    }),

    // ======================
    // GET LATEST NEWS
    // ======================
    getLatestNews: builder.query({
      query: ({ limit = 10 } = {}) => `/news-latest?limit=${limit}`,
      providesTags: ["News"],
    }),

    // ======================
    // GET FEATURED NEWS
    // ======================
    getFeaturedNews: builder.query({
      query: ({ limit = 5 } = {}) => `/news-featured?limit=${limit}`,
      providesTags: ["News"],
    }),

    // ======================
    // GET BREAKING NEWS
    // ======================
    getBreakingNews: builder.query({
      query: ({ limit = 3 } = {}) => `/news-breaking?limit=${limit}`,
      providesTags: ["News"],
    }),

    // ======================
    // GET NEWS BY CATEGORY
    // ======================
    getNewsByCategory: builder.query({
      query: ({ category, limit = 10, offset = 0 }) =>
        `/news-category/${category}?limit=${limit}&offset=${offset}`,
      providesTags: (result, error, { category }) => [
        { type: "News", category },
      ],
    }),

    // ======================
    // GET ALL CATEGORIES
    // ======================
    getNewsCategories: builder.query({
      query: () => "/news-categories",
      providesTags: ["NewsCategories"],
    }),

    // ======================
    // SEARCH NEWS
    // ======================
    searchNews: builder.query({
      query: ({ q, limit = 10 }) => `/news-search?q=${q}&limit=${limit}`,
      providesTags: ["News"],
    }),

    // ======================
    // GET RELATED NEWS
    // ======================
    getRelatedNews: builder.query({
      query: ({ id, limit = 5 }) => `/news-related/${id}?limit=${limit}`,
      providesTags: (result, error, { id }) => [{ type: "News", id }],
    }),

    // ======================
    // UPDATE NEWS
    // ======================
    updateNews: builder.mutation({
      query: ({ id, data }) => ({
        url: `/news-update/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "News", id },
        { type: "News", id: "LIST" },
      ],
    }),

    // ======================
    // DELETE NEWS
    // ======================
    deleteNews: builder.mutation({
      query: (id) => ({
        url: `/news-delete/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "News", id },
        { type: "News", id: "LIST" },
      ],
    }),

    // ======================
    // CHANGE NEWS STATUS
    // ======================
    changeStatusNews: builder.mutation({
      query: (id) => ({
        url: `/news-status/${id}`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "News", id },
        { type: "News", id: "LIST" },
      ],
    }),
  }),
});

// ======================
// EXPORT HOOKS
// ======================
export const {
  // Mutations
  useCreateNewsMutation,
  useUpdateNewsMutation,
  useDeleteNewsMutation,
  useChangeStatusNewsMutation,

  // Queries
  useGetNewsQuery,
  useGetSingleNewsQuery,
  useGetNewsBySlugQuery,
  useGetLatestNewsQuery,
  useGetFeaturedNewsQuery,
  useGetBreakingNewsQuery,
  useGetNewsByCategoryQuery,
  useGetNewsCategoriesQuery,
  useSearchNewsQuery,
  useGetRelatedNewsQuery,
} = NewsApi;

export default NewsApi;
